import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import { categories, latestMovies, hotMovies } from "@/mocks/movies";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Home() {
  useDocumentTitle({
    title: "屁屁影院-最新电影电视剧在线播放平台",
    description:
      "屁屁影院提供丰富的电影、电视剧资源，涵盖多种类型，满足用户在线观看需求。访问屁屁影院轻松发现热门影视作品和精彩内容。",
    keywords: "屁屁影院,电影网,在线观看电影,电视剧播放,影视资源,免费视频,影视平台",
  });

  const topLatest = latestMovies.slice(0, 10);
  const topHot = hotMovies.slice(0, 8);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <HeroCarousel />

        <section id="categories" className="px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeading title="影视分类" subtitle="按类型快速找到你想看的内容" icon="ri-layout-grid-line" />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
              {categories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="group flex flex-col gap-3 rounded-lg border border-background-200/60 bg-background-100 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-accent-500/70 md:p-5"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500/15 text-primary-300 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
                    <i className={`${c.icon} text-2xl`} />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{c.label}</h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-500">{c.desc}</p>
                  </div>
                  <span className="mt-auto flex items-center gap-1 text-xs font-medium text-accent-400">
                    进入频道
                    <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-background-200/60 bg-background-100/40 px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeading
              title="最新上线"
              subtitle="近期更新的影视作品，第一时间追上"
              icon="ri-time-line"
              action={{ label: "查看全部", to: "/category/film" }}
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {topLatest.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-12 md:px-6 md:py-16">
          <div className="mx-auto max-w-[1280px]">
            <SectionHeading
              title="热门推荐"
              subtitle="按播放热度与评分精选的高人气作品"
              icon="ri-fire-line"
              action={{ label: "更多热门", to: "/category/tv" }}
            />
            <div className="grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-2">
              {topHot.map((movie, i) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex gap-4 rounded-lg border border-background-200/60 bg-background-100 p-3 transition-all duration-300 hover:border-accent-500/70 md:p-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-200 font-heading text-lg font-bold text-accent-400">
                    {i + 1}
                  </span>
                  <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md">
                    <img
                      src={movie.cover}
                      alt={`${movie.title}封面`}
                      title={`${movie.title} ${movie.genres.join(" ")}`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="truncate text-base font-semibold text-foreground-950 transition-colors group-hover:text-accent-400">
                        {movie.title}
                      </h3>
                      <span className="shrink-0 font-heading text-sm font-bold text-accent-400">{movie.rating.toFixed(1)}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-500">
                      {movie.typeLabel} · {movie.year} · {movie.region} · {movie.genres.join(" / ")}
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-500">{movie.synopsis}</p>
                    <p className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-fire-fill text-accent-500" />
                      热度 {movie.views}万
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-background-200/60 bg-background-100/40 px-4 py-14 md:px-6 md:py-16">
          <div className="mx-auto max-w-[1280px]">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                { icon: "ri-device-line", title: "多端畅享", desc: "电脑、平板、手机自适应浏览，随时随地发现新片。" },
                { icon: "ri-search-eye-line", title: "精准筛选", desc: "按类型、地区、年代与热度快速定位心仪影视。" },
                { icon: "ri-bookmark-3-line", title: "本地收藏", desc: "一键收藏喜欢的作品，无需登录也能保存记录。" },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
                    <i className={`${f.icon} text-2xl`} />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-950">{f.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}